import React, { useState, useEffect } from "react";

const HeadingForAll = (props) => {
  const handleNavigate = (path) => {
    // For demo purposes, you'll need to replace this with actual navigation
    console.log(`Navigate to: ${path}`);
  };
  
  return (
    <div 
      className="bg-[#10c9c0] flex justify-center relative"
      style={{
        maxHeight: '35vh',
        minHeight: 'fit-content'
      }}
    >
      {props.isProfile && (
        <button
          onClick={() => handleNavigate("/profile-settings")}
          className="absolute right-2 bottom-2"
        >
        </button>
      )}
      <div 
        className="md:ml-[3rem] ml-[1.5rem] mr-[1.5rem] text-white w-full max-w-[1440px] flex flex-col justify-center"
        style={{
          paddingTop: 'clamp(0.5rem, 2vmin, 1rem)',
          paddingBottom: 'clamp(1rem, 3vmin, 2.5rem)'
        }}
      >
        <h2 
          className="m-0 font-bold"
          style={{
            fontSize: 'clamp(12px, 2.5vmin, 24px)',
            paddingTop: 'clamp(0.25rem, 1vmin, 0.5rem)'
          }}
        >
          {props.smallHeading}
        </h2>
        
        <h1 
          className="m-0 font-bold"
          style={{
            fontSize: 'clamp(24px, 8vmin, 72px)',
            lineHeight: 'clamp(28px, 9vmin, 78px)',
            paddingTop: 'clamp(0.25rem, 1vmin, 0.5rem)',
            paddingBottom: 'clamp(0.25rem, 1vmin, 0.5rem)'
          }}
        >
          {props.bigHeading}
        </h1>
      </div>
    </div>
  );
};

const AgentsDirectory = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('experience');
  const [filterBySpecialization, setFilterBySpecialization] = useState('all');
  const [imageErrors, setImageErrors] = useState(new Set());

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://app.akiya2.com/agents/get-all-agents');
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (err) {
      setError('Failed to load agents');
      console.error('Error fetching agents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageError = (agentId) => {
    setImageErrors(prev => new Set([...prev, agentId]));
  };

  const getAllSpecializations = () => {
    const specializations = new Set();
    agents.forEach(agent => {
      agent.specializations?.forEach(spec => specializations.add(spec));
    });
    return Array.from(specializations);
  };

  const filteredAndSortedAgents = agents
    .filter(agent => {
      const matchesSearch = agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           agent.business_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           agent.bio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           agent.specializations?.some(spec => spec.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesSpecialization = filterBySpecialization === 'all' || 
                                   agent.specializations?.includes(filterBySpecialization);
      
      return matchesSearch && matchesSpecialization;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'experience':
          return (b.years_experience || 0) - (a.years_experience || 0);
        case 'listings':
          return (b.listings_added?.length || 0) - (a.listings_added?.length || 0);
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });

  const handleAgentClick = (agentId) => {
    window.location.href = `/agent/${agentId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#fafafa' }}>
        <HeadingForAll 
          smallHeading="Our Team"
          bigHeading="Meet Our Professional Agents"
        />
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center animate-pulse"
              style={{ background: 'linear-gradient(to bottom right, #10c9c0, #0eb1a9)' }}>
              <i className="fas fa-users text-white text-2xl"></i>
            </div>
            <p style={{ 
              fontWeight: '300',
              fontSize: 'clamp(14px, 4.5vw, 20px)',
              color: '#949995'
            }}>Loading our agents...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#fafafa' }}>
        <HeadingForAll 
          smallHeading="Our Team"
          bigHeading="Meet Our Professional Agents"
        />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
            <p className="text-red-500 font-bold text-xl mb-4">{error}</p>
            <button 
              onClick={fetchAgents}
              className="mt-4 px-6 py-3 bg-[#10c9c0] text-white rounded-lg hover:opacity-90 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fafafa' }}>
      <style>{`
        .agent-card {
          transition: all 0.3s ease;
        }
        .agent-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        .specialization-tag {
          background: linear-gradient(to right, #10c9c0, #0eb1a9);
          color: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }
        .search-input {
          border: 1px solid rgba(148, 153, 149, 0.3);
        }
        .search-input:focus {
          outline: none;
          border-color: #10c9c0;
          box-shadow: 0 0 0 2px rgba(16, 201, 192, 0.2);
        }
        .stats-card {
          background: rgba(16, 201, 192, 0.05);
          border: 1px solid rgba(16, 201, 192, 0.2);
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .agent-image {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid rgba(16, 201, 192, 0.2);
        }
        .agent-image-fallback {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: linear-gradient(to bottom right, #10c9c0, #0eb1a9);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
          font-weight: bold;
        }
      `}</style>

      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
      />

      <HeadingForAll 
        smallHeading="Our Professional Network"
        bigHeading="Trusted Real Estate Agents"
      />

      <div className="mx-auto py-8 max-w-[1440px]" style={{ padding: '32px clamp(6px, 4vw, 30px)' }}>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8" style={{ border: '1px solid rgba(16, 201, 192, 0.2)' }}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 search-input rounded-lg"
                  placeholder="Search agents by name, location, or bio..."
                />
                <i className="fas fa-search absolute left-3 top-4 text-gray-400"></i>
              </div>
            </div>

            {/* Filters */}
            <div className="flex space-x-4">
              <select
                value={filterBySpecialization}
                onChange={(e) => setFilterBySpecialization(e.target.value)}
                className="px-4 py-3 search-input rounded-lg"
              >
                <option value="all">All Specializations</option>
                {getAllSpecializations().map(spec => (
                  <option key={spec} value={spec}>{spec}</option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 search-input rounded-lg"
              >
                <option value="experience">Most Experienced</option>
                <option value="listings">Most Listings</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>
          </div>
        </div>

        {/* Agents Grid */}
        {filteredAndSortedAgents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAndSortedAgents.map((agent) => (
              <div
                key={agent.agent_id}
                onClick={() => handleAgentClick(agent.agent_id)}
                className="agent-card bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer"
                style={{ border: '1px solid rgba(16, 201, 192, 0.2)' }}
              >
                {/* Agent Header */}
                <div className="p-6 pb-4">
                  <div className="flex items-start justify-between mb-4">
                    {/* Agent Image or Fallback */}
                    {agent.image_url && !imageErrors.has(agent.agent_id) ? (
                      <img
                        src={agent.image_url}
                        alt={agent.name}
                        className="agent-image"
                        onError={() => handleImageError(agent.agent_id)}
                      />
                    ) : (
                      <div className="agent-image-fallback">
                        {agent.name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                    )}
                    
                    <div className="text-right">
                      <div className="flex items-center space-x-1 text-sm" style={{ color: '#949995' }}>
                        <i className="fas fa-building"></i>
                        <span>{agent.listings_added?.length || 0} listings</span>
                      </div>
                    </div>
                  </div>

                  <h3 className="mb-2 font-bold text-xl" style={{ 
                    color: '#000',
                    fontSize: 'clamp(16px, 6vw, 28px)'
                  }}>
                    {agent.name}
                  </h3>
                  
                  <p className="text-sm mb-3" style={{ color: '#949995' }}>
                    <i className="fas fa-map-marker-alt mr-2"></i>
                    {agent.business_address}
                  </p>

                  {/* Experience and License */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4 text-sm" style={{ color: '#949995' }}>
                      <span>
                        <i className="fas fa-medal mr-1"></i>
                        {agent.years_experience || 0} years
                      </span>
                      <span>
                        <i className="fas fa-certificate mr-1"></i>
                        {agent.license_number}
                      </span>
                    </div>
                  </div>

                  {/* Bio */}
                  <p className="text-sm mb-4 line-clamp-3" style={{ 
                    fontWeight: '300',
                    color: '#949995'
                  }}>
                    {agent.bio}
                  </p>

                  {/* Specializations */}
                  {agent.specializations && agent.specializations.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {agent.specializations.slice(0, 3).map((spec, index) => (
                          <span key={index} className="specialization-tag">
                            {spec}
                          </span>
                        ))}
                        {agent.specializations.length > 3 && (
                          <span className="text-xs" style={{ color: '#949995' }}>
                            +{agent.specializations.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Languages */}
                  {agent.languages_spoken && agent.languages_spoken.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs mb-2" style={{ color: '#949995' }}>
                        <i className="fas fa-language mr-1"></i>
                        Languages: {agent.languages_spoken.slice(0, 3).join(', ')}
                        {agent.languages_spoken.length > 3 && ` +${agent.languages_spoken.length - 3} more`}
                      </p>
                    </div>
                  )}

                  {/* Social Links */}
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-3">
                      {agent.website && (
                        <a
                          href={agent.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm hover:opacity-70"
                          style={{ color: '#10c9c0' }}
                        >
                          <i className="fas fa-globe"></i>
                        </a>
                      )}
                      {agent.linkedin_link && (
                        <a
                          href={agent.linkedin_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm hover:opacity-70"
                          style={{ color: '#10c9c0' }}
                        >
                          <i className="fab fa-linkedin"></i>
                        </a>
                      )}
                      {agent.facebook_link && (
                        <a
                          href={agent.facebook_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm hover:opacity-70"
                          style={{ color: '#10c9c0' }}
                        >
                          <i className="fab fa-facebook"></i>
                        </a>
                      )}
                      {agent.twitter_link && (
                        <a
                          href={agent.twitter_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm hover:opacity-70"
                          style={{ color: '#10c9c0' }}
                        >
                          <i className="fab fa-twitter"></i>
                        </a>
                      )}
                    </div>

                    <button
                      className="px-4 py-2 text-white rounded-lg transition-all text-sm bg-[#10c9c0] hover:bg-[--sea-green]"
                    >
                      View Profile
                      <i className="fas fa-arrow-right ml-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(16, 201, 192, 0.1)' }}>
              <i className="fas fa-search text-4xl opacity-50" style={{ color: '#10c9c0' }}></i>
            </div>
            <h3 className="mb-4 font-bold text-xl" style={{ 
              color: '#000',
              fontSize: 'clamp(16px, 6vw, 28px)'
            }}>
              No agents found
            </h3>
            <p className="mb-6" style={{ 
              fontWeight: '300',
              fontSize: 'clamp(14px, 4.5vw, 20px)',
              color: '#949995'
            }}>
              Try adjusting your search criteria or filters
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterBySpecialization('all');
              }}
              className="px-6 py-3 text-white rounded-lg hover:opacity-90 transition-colors"
              style={{ background: 'linear-gradient(to right, #10c9c0, #0eb1a9)' }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentsDirectory;